import * as turf from "@turf/turf";
import type { FeatureCollection } from "geojson";
import type mapboxgl from "mapbox-gl";
import type { Expression } from "mapbox-gl";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { flattenToPoints } from "../utils/flattenToPoints";

interface UseHeatMapProps {
  map: mapboxgl.Map | null;
  geoData: FeatureCollection;
  type: "heatmap" | "hexabin";
  cellSize?: number;
  mapbounds: mapboxgl.LngLatBounds;
  heatmapStyle: {
    intensity: number;
    style: "heatmap" | "hexabin" | "drawn";
  };
  setLabelsData: (data: any) => void;
}

const defaultCellSize = 1;

function countCoordinatesInFeatureCollection(featureCollection) {
  let coordinateCount = 0;

  for (const feature of featureCollection.features) {
    if (feature.geometry) {
      const geometryType = feature.geometry.type;

      switch (geometryType) {
        case "Point":
        case "MultiPoint":
          coordinateCount += 1;
          break;

        case "LineString":
        case "MultiLineString":
          coordinateCount += feature.geometry.coordinates.length;
          break;

        case "Polygon":
        case "MultiPolygon":
          for (const polygonCoordinates of feature.geometry.coordinates) {
            coordinateCount += polygonCoordinates.length;
          }
          break;

        default:
          break;
      }
    }
  }

  return coordinateCount;
}

const getHeatMapRadius = ([start, end]: number[]) => {
  return [
    "interpolate",
    ["exponential", 2],
    ["zoom"],
    0,
    start * 0.75,
    18,
    end * 7.5,
  ] as Expression;
};

const getHeatMapIntensity = (intensity: number) => {
  return [
    "interpolate",
    ["exponential", 2],
    ["zoom"],
    0,
    intensity / 125,
    18,
    intensity / 125,
  ] as Expression;
};

const getHeatMapColor = () => {
  return [
    "interpolate",
    ["linear"],
    ["heatmap-density"],
    0,
    "rgba(255,208,97,0)",
    0.1,
    "rgba(255,208,97,0.25)",
    0.2,
    "rgba(255,208,97,0.5)",
    0.3,
    "rgba(255,192,83,0.6)",
    0.4,
    "rgba(255,182,70,0.7)",
    0.5,
    "rgba(255,170,57,0.8)",
    0.6,
    "rgba(255,157,44,0.8)",
    0.7,
    "rgba(247,133,33,0.85)",
    0.8,
    "rgba(239,101,29,0.9)",
    0.9,
    "rgba(208,52,36,0.95)",
    1,
    "rgba(178,24,43,1)",
  ] as Expression;
};
const useHeatMap = ({
  map,
  geoData,
  type,
  cellSize = defaultCellSize,
  mapbounds,
  heatmapStyle,
  setLabelsData,
}: UseHeatMapProps) => {
  const prevType = useRef<string | null>(null);
  const sourceId = "heatmap-source";
  const layerId = "heatmap-layer";

  const flatGeoData = useMemo(() => {
    return flattenToPoints(mapbounds, geoData);
  }, [geoData, mapbounds]);

  const coordCount = useMemo(() => {
    return countCoordinatesInFeatureCollection(flatGeoData);
  }, [flatGeoData]);

  // Check if the flattened data contains points from areas (polygons)
  const hasAreaPoints = useMemo(() => {
    return flatGeoData.features.some(
      (feature) => feature.properties?._fromPolygon === true,
    );
  }, [flatGeoData]);

  const hasPathPoints = useMemo(() => {
    return flatGeoData.features.some(
      (feature) => feature.properties?._fromLine === true,
    );
  }, [flatGeoData]);

  const updateHeatmap = useCallback(() => {
    if (!map || (!map?._loaded && geoData)) {
      return;
    }

    if (heatmapStyle.style === "drawn") {
      setLabelsData(geoData);
      return;
    }

    const sourceExists = map.getSource(sourceId);
    const layerExists = map.getLayer(layerId);

    if (type !== prevType.current) {
      if (layerExists) map.removeLayer(layerId);
      if (sourceExists) map.removeSource(sourceId);
      prevType.current = type;
    }

    if (type === "heatmap") {
      const factor = (() => {
        if (coordCount < 5000) return 1;
        if (coordCount < 10000) return 0.7;
        if (coordCount < 20000) return 0.3;
        if (coordCount < 100000) return 0.27;
        return 0.25;
      })();

      // Adjust intensity and radius based on whether we have area points
      let intensityMultiplier = 1;
      let radiusMultiplier = 1;

      if (hasAreaPoints) {
        intensityMultiplier = 0.375;
      } else if (hasPathPoints) {
        radiusMultiplier = 0.5;
      } else {
        intensityMultiplier = 2;
      }

      const finalIntensity = heatmapStyle?.intensity * factor;
      const finalRadius = Number(heatmapStyle?.intensity || 0.1);

      if (!sourceExists) {
        map.addSource(sourceId, {
          type: "geojson",
          data: flatGeoData,
        });
      } else {
        (map.getSource(sourceId) as mapboxgl.GeoJSONSource).setData(
          flatGeoData,
        );
      }

      if (!layerExists) {
        console.log("Adding heatmap layer");
        map.addLayer({
          id: layerId,
          type: "heatmap",
          source: sourceId,
          paint: {
            "heatmap-opacity": 0.7,
            "heatmap-radius": getHeatMapRadius([
              intensityMultiplier,
              finalRadius * radiusMultiplier,
            ]),
            "heatmap-intensity": getHeatMapIntensity(
              finalIntensity * intensityMultiplier,
            ),
            "heatmap-color": getHeatMapColor(),
          },
        });
      } else {
        console.log("Updating heatmap layer");
        map.setPaintProperty(
          layerId,
          "heatmap-radius",
          getHeatMapRadius([
            intensityMultiplier,
            finalRadius * radiusMultiplier,
          ]),
        );
        map.setPaintProperty(
          layerId,
          "heatmap-intensity",
          getHeatMapIntensity(finalIntensity * intensityMultiplier),
        );
      }
    }

    // Optional: handle "hexabin" or empty type separately
  }, [
    geoData,
    flatGeoData,
    coordCount,
    hasAreaPoints,
    hasPathPoints,
    heatmapStyle,
    map,
    setLabelsData,
    type,
  ]);

  useEffect(() => {
    updateHeatmap();
  }, [updateHeatmap]);

  useEffect(() => {
    return () => {
      if (map?.getLayer(layerId)) map.removeLayer(layerId);
      if (map?.getSource(sourceId)) map.removeSource(sourceId);
    };
  }, [map]);
};

export default useHeatMap;
